import React from "react";
import { Container, Row, Col, Card, Button, ListGroup, Form } from "react-bootstrap";


// Optional: pass logo path as prop
function DigitalTwin() {
  return (
    <div className="dt-page">
      {/* <header className="dt-hero py-5">
        <Container className="d-flex align-items-center gap-4">
          <img src={logoSrc} alt="Innoish" className="dt-logo" />
          <div>
            <h1 className="dt-title">
              Digital Twins for Warehouse, Factory & Process Automation
            </h1>
            <p className="dt-lead">
              We create high-fidelity Digital Twin platforms that let you validate operations, commission PLCs virtually,
              and bring software systems to life — before hardware is ready.
            </p>
            <div className="d-flex gap-2 mt-3">
              <Button href="#demo" className="btn-gradient">Request Demo</Button>
              <Button variant="outline-light" href="#contact">Contact Sales</Button>
            </div>
          </div>
        </Container>
      </header>

      <main> */}
        <section className="py-5">
          <Container>
            <Row>
              <Col md={8}>
                <h3>Where We Build Digital Twins</h3>
                <p className="text-muted">
                  Our Digital Twins are tailored for warehouse automation, factory automation, and process industries — enabling design validation,
                  throughput optimisation, and operator training.
                </p>

                <Row className="mt-4">
                  <Col md={4}>
                    <Card className="mb-3 dt-card">
                      <Card.Body>
                        <Card.Title>Warehouse Automation</Card.Title>
                        <Card.Text className="small text-muted">
                          AMR/AGV fleets, ASRS, sortation, picking & packing, conveyors, and intralogistics orchestration.
                        </Card.Text>
                        <ListGroup variant="flush" className="mt-2 small">
                          <ListGroup.Item>Material flow & throughput</ListGroup.Item>
                          <ListGroup.Item>Robot coordination & deadlock avoidance</ListGroup.Item>
                          <ListGroup.Item>Order routing & storage strategies</ListGroup.Item>
                        </ListGroup>
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col md={4}>
                    <Card className="mb-3 dt-card">
                      <Card.Body>
                        <Card.Title>Factory Automation</Card.Title>
                        <Card.Text className="small text-muted">
                          Assembly lines, packaging, material handling, and mechatronic machines.
                        </Card.Text>
                        <ListGroup variant="flush" className="mt-2 small">
                          <ListGroup.Item>Cycle-time & collision checks</ListGroup.Item>
                          <ListGroup.Item>Layout & production balancing</ListGroup.Item>
                          <ListGroup.Item>Control-strategy testing</ListGroup.Item>
                        </ListGroup>
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col md={4}>
                    <Card className="mb-3 dt-card">
                      <Card.Body>
                        <Card.Title>Process Automation</Card.Title>
                        <Card.Text className="small text-muted">
                          Chemical, thermal, fluid, and batch processes — model dynamics & control behavior.
                        </Card.Text>
                        <ListGroup variant="flush" className="mt-2 small">
                          <ListGroup.Item>PID & control logic simulation</ListGroup.Item>
                          <ListGroup.Item>Bottleneck & buffer analysis</ListGroup.Item>
                          <ListGroup.Item>Equipment dynamics</ListGroup.Item>
                        </ListGroup>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                <h4 className="mt-4">Virtual Commissioning (PLC & Software)</h4>
                <p className="text-muted">
                  We connect actual PLC logic and higher-level software (WES, WMS, FMS, MES, SCADA) to the virtual model — enabling early testing of sequences,
                  interlocks, safety logic, and exception handling without physical hardware.
                </p>

                <ul className="dt-list">
                  <li><strong>PLC Virtual Commissioning</strong> — S7, TwinCAT, Studio 5000, Codesys; test I/O, networked sensors, and robot controllers.</li>
                  <li><strong>Software Commissioning</strong> — Validate APIs, task allocation, fleet behaviour, and order flows for WMS/WES/FMS.</li>
                  <li><strong>Failure & Edge Cases</strong> — Simulate breakdowns, peak loads, network delays and confirm recovery strategies.</li>
                </ul>
              </Col>

              <Col md={4}>
                <Card className="p-3 contact-card">
                  <h5>Use Digital Twins as a Platform</h5>
                  <p className="small text-muted">
                    Connect sensors, PLCs and software; run what-if scenarios; train operators and validate AI models before field deployment.
                  </p>

                  <ListGroup className="mb-3 small">
                    <ListGroup.Item>Live data sync & playback</ListGroup.Item>
                    <ListGroup.Item>Scenario orchestration</ListGroup.Item>
                    <ListGroup.Item>Training & validation</ListGroup.Item>
                  </ListGroup>

                  <Button href="#demo" className="btn-gradient w-100">Schedule a Demo</Button>
                </Card>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Technology Stack */}
        <section className="py-5 bg-light">
          <Container>
            <h3>Our Technologies for Digital Twin Development</h3>
            <Row className="mt-3 g-3">
              <Col md={4}>
                <Card className="h-100 tech-card">
                  <Card.Body>
                    <Card.Title>Siemens Tecnomatix Plant Simulation</Card.Title>
                    <Card.Text className="small text-muted">
                      Industry-grade discrete-event and material flow simulation for logistics and production. Ideal for throughput studies,
                      capacity planning and SimTalk-driven custom logic.
                    </Card.Text>
                    <ul className="small dt-list mt-2">
                      <li>High-fidelity material flow & buffering</li>
                      <li>Scripting & custom resource models</li>
                      <li>Capacity planning & ROI scenarios</li>
                    </ul>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={4}>
                <Card className="h-100 tech-card">
                  <Card.Body>
                    <Card.Title>NVIDIA Omniverse</Card.Title>
                    <Card.Text className="small text-muted">
                      Real-time, photorealistic 3D platform for robotics simulation, physics-accurate testing, and perception validation.
                      Integrates with ROS, Isaac Sim and USD pipelines.
                    </Card.Text>
                    <ul className="small dt-list mt-2">
                      <li>PhysX-driven robot & environment physics</li>
                      <li>High-fidelity visualization & operator training</li>
                      <li>AI perception & sensor testing</li>
                    </ul>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={4}>
                <Card className="h-100 tech-card">
                  <Card.Body>
                    <Card.Title>PySim (Python Simulation Framework)</Card.Title>
                    <Card.Text className="small text-muted">
                      Our lightweight Python stack for rapid prototyping, algorithm testing, and integrating AI/ML workflows with digital twins.
                    </Card.Text>
                    <ul className="small dt-list mt-2">
                      <li>Fast iteration for control & fleet algorithms</li>
                      <li>API-first, MQTT/OPC-UA/REST integration</li>
                      <li>Perfect for SaaS-style simulations & microservices</li>
                    </ul>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Container>
        </section>
        
            {/* </main> */}
            </div>  
  );
}
export default DigitalTwin;